export interface SelectOption {
  value: string;
  label: string;
}

const formatHourLabel = (hour: number): string => {
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:00 ${period}`;
};

export const TIME_OPTIONS: SelectOption[] = Array.from(
  { length: 24 },
  (_, hour) => ({
    value: `${hour.toString().padStart(2, "0")}:00`,
    label: formatHourLabel(hour),
  }),
);

/*
 * Hotel timezone is an IANA zone name (e.g. "Europe/Madrid") so that daylight
 * saving time applies. Legacy fixed-offset labels "GMT±H:00" map to the
 * POSIX-style Etc zones with the sign inverted: "GMT+1:00" is "Etc/GMT-1".
 * Mirrors guesteria-backend src/hotel/dto/hotel-timezone.util.ts.
 */

const LEGACY_GMT_OFFSET = /^GMT([+-])(\d{1,2}):(\d{2})$/;
const IANA_NAME = /^[A-Z][A-Za-z0-9_+-]*(?:\/[A-Z][A-Za-z0-9_+-]*)*$/;

type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: "timeZone") => string[];
};

/** Maps a legacy "GMT±H:00" label to "Etc/GMT∓H"; other values are returned unchanged. */
export const normalizeTimezone = (value: string): string => {
  const match = LEGACY_GMT_OFFSET.exec(value.trim());
  if (!match) return value;
  const [, sign, hoursRaw, minutes] = match;
  const hours = Number(hoursRaw);
  if (minutes !== "00" || hours > 14) return value;
  if (hours === 0) return "Etc/GMT";
  return `Etc/GMT${sign === "+" ? "-" : "+"}${hours}`;
};

/** True for an IANA zone known to the browser tz database (aliases, UTC and Etc/GMT±H included). */
export const isValidTimezone = (value: string): boolean => {
  if (!IANA_NAME.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

/** Current UTC offset of a zone, DST-aware: "UTC+02:00". */
export const formatUtcOffset = (
  timeZone: string,
  at: Date = new Date(),
): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    } as Intl.DateTimeFormatOptions).formatToParts(at);
    const name =
      parts.find((part) => part.type === "timeZoneName")?.value ?? "";
    const offset = name.replace(/^GMT/, "");
    return `UTC${offset || "+00:00"}`;
  } catch {
    return "";
  }
};

const getSupportedTimezones = (): string[] => {
  const supportedValuesOf = (Intl as IntlWithSupportedValues).supportedValuesOf;
  return supportedValuesOf ? supportedValuesOf("timeZone") : [];
};

const toTimezoneOption = (timeZone: string, at: Date): SelectOption => {
  const offset = formatUtcOffset(timeZone, at);
  return {
    value: timeZone,
    label: offset ? `${timeZone} (${offset})` : timeZone,
  };
};

/**
 * IANA zones from Intl.supportedValuesOf("timeZone") plus UTC, labelled with
 * the current offset. A valid current value missing from the list (an alias
 * or a migrated Etc/GMT±H) is kept so the select can show it.
 */
export const getTimezoneOptions = (
  currentValue?: string,
  at: Date = new Date(),
): SelectOption[] => {
  const zones = new Set(getSupportedTimezones());
  zones.add("UTC");
  if (currentValue && isValidTimezone(currentValue)) {
    zones.add(currentValue);
  }
  return Array.from(zones)
    .sort((a, b) => a.localeCompare(b))
    .map((zone) => toTimezoneOption(zone, at));
};

const normalizeSearch = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, " ");

/** Case-insensitive search over zone name and offset label; "new york" matches America/New_York. */
export const filterTimezoneOptions = (
  options: SelectOption[],
  query: string,
): SelectOption[] => {
  const needle = normalizeSearch(query);
  if (!needle) return options;
  return options.filter((option) =>
    normalizeSearch(option.label).includes(needle),
  );
};
